import { useMemo, useState } from 'react';
import { ProductWithCalculations, PurchaseOrderItem, StatusType } from '../shared/types/inventory';
import { Badge } from '../shared/components/Badge';
import { getStatusInfo } from '../shared/utils/inventoryCalculations';
import {
  ChevronDown, ChevronRight, Building2, Package, TrendingUp,
  AlertCircle, Search, X, FileSpreadsheet,
} from 'lucide-react';
import * as XLSX from 'xlsx';

function formatCNPJ(cnpj: string): string {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

function buildPurchaseOrderItems(products: ProductWithCalculations[]): PurchaseOrderItem[] {
  return products
    .filter((p) => p.pedidoSugerido > 0)
    .map((product) => {
      const quantidade = Math.ceil(product.pedidoSugerido);
      const precoUnitario = product.precoUnitario || 0;

      let prioridade: 'alta' | 'media' | 'baixa' = 'baixa';
      if (product.status === 'ruptura' || product.status === 'iminente') {
        prioridade = 'alta';
      } else if (product.status === 'minimo' || product.status === 'critica') {
        prioridade = 'media';
      }

      return {
        product,
        quantidade,
        precoUnitario,
        subtotal: quantidade * precoUnitario,
        fornecedor: product.fornecedor || 'Fornecedor nao informado',
        prioridade,
      };
    })
    .sort((a, b) => {
      const order = { alta: 0, media: 1, baixa: 2 };
      return order[a.prioridade] - order[b.prioridade];
    });
}

function generateXLSX(items: PurchaseOrderItem[], cnpj: string) {
  const headerRows = [
    ['ATENCAO: Nao remova o cabecalho desta planilha (linhas 1 a 4).'],
    ['Passo 1: Insira um SKU (codigo de barras) por linha e a respectiva quantidade desejada.'],
    ['Passo 2: Salve o arquivo, mantenha o formato original (.xlsx) e importe na plataforma.'],
    ['SKU (codigo de barras)', 'Quantidade desejada'],
  ];

  const dataRows = items.map((item) => [item.product.ean, item.quantidade]);
  const allRows = [...headerRows, ...dataRows];

  const ws = XLSX.utils.aoa_to_sheet(allRows);

  ws['!cols'] = [{ wch: 25 }, { wch: 22 }];

  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 1 } },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pedido');
  XLSX.writeFile(wb, `pedido_${cnpj}_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

interface PurchaseOrderCNPJSectionProps {
  cnpj: string;
  razaoSocial: string;
  products: ProductWithCalculations[];
  isExpanded: boolean;
  onToggle: () => void;
}

const getPrioridadeBadge = (prioridade: 'alta' | 'media' | 'baixa') => {
  const configs = {
    alta: { label: 'Alta Prioridade', color: 'text-red-900', bgColor: 'bg-red-100' },
    media: { label: 'Media Prioridade', color: 'text-yellow-900', bgColor: 'bg-yellow-100' },
    baixa: { label: 'Baixa Prioridade', color: 'text-gray-900', bgColor: 'bg-gray-100' },
  };
  const config = configs[prioridade];
  return <Badge label={config.label} color={config.color} bgColor={config.bgColor} />;
};

export const PurchaseOrderCNPJSection = ({
  cnpj, razaoSocial, products, isExpanded, onToggle,
}: PurchaseOrderCNPJSectionProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const orderItems = useMemo(() => buildPurchaseOrderItems(products), [products]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return orderItems;
    const q = searchQuery.toLowerCase().trim();
    return orderItems.filter(
      (item) =>
        item.product.nome.toLowerCase().includes(q) ||
        item.product.ean.toLowerCase().includes(q)
    );
  }, [orderItems, searchQuery]);

  const totals = useMemo(() => {
    const totalItens = orderItems.length;
    const totalUnidades = orderItems.reduce((sum, item) => sum + item.quantidade, 0);
    const alta = orderItems.filter((i) => i.prioridade === 'alta').length;
    const media = orderItems.filter((i) => i.prioridade === 'media').length;
    return { totalItens, totalUnidades, alta, media };
  }, [orderItems]);

  const handleExportXLSX = () => {
    if (orderItems.length === 0) return;
    generateXLSX(orderItems, cnpj.replace(/\D/g, ''));
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-gray-500">
            {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </div>
          <div className="p-2 rounded-lg bg-teal-50 border border-teal-100">
            <Building2 className="w-5 h-5 text-teal-600" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-3">
              <h3 className="text-base font-bold text-gray-900 font-mono tracking-wide">
                {formatCNPJ(cnpj)}
              </h3>
              {razaoSocial && (
                <span className="text-sm text-gray-500 font-normal">{razaoSocial}</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {totals.totalItens} {totals.totalItens === 1 ? 'item' : 'itens'} para reposicao
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {totals.alta > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-100">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              {totals.alta} urgente{totals.alta > 1 ? 's' : ''}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-100">
            {totals.totalUnidades} un
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100">
          <div className="px-6 pt-5 pb-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Itens Diferentes</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{totals.totalItens}</p>
                  </div>
                  <div className="p-2.5 rounded-full bg-blue-100">
                    <Package className="w-5 h-5 text-blue-700" />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Unidades</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{totals.totalUnidades}</p>
                  </div>
                  <div className="p-2.5 rounded-full bg-teal-100">
                    <TrendingUp className="w-5 h-5 text-teal-700" />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Urgentes</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{totals.alta}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{totals.media} medias</p>
                  </div>
                  <div className="p-2.5 rounded-full bg-red-100">
                    <AlertCircle className="w-5 h-5 text-red-700" />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4 flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou EAN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent shadow-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <span className="text-sm text-gray-500">
                {filteredItems.length} de {orderItems.length} itens
              </span>
              <div className="ml-auto">
                <button
                  onClick={handleExportXLSX}
                  disabled={orderItems.length === 0}
                  className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Gerar Pedido (XLSX)
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-y border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Produto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status Atual
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Quantidade
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Prioridade
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Package className="w-8 h-8 text-gray-300" />
                        <p className="text-gray-500 font-medium">Nenhum item encontrado</p>
                        <p className="text-gray-400 text-sm">
                          {orderItems.length === 0
                            ? 'Todos os produtos estao com estoque adequado'
                            : 'Tente ajustar a busca'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => {
                    const statusInfo = getStatusInfo(item.product.status);
                    return (
                      <tr key={`${cnpj}-${item.product.id}`} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{item.product.nome}</p>
                            <p className="text-xs text-gray-500">{item.product.ean}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge label={statusInfo.label} color={statusInfo.color} bgColor={statusInfo.bgColor} />
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                          {item.quantidade} un
                        </td>
                        <td className="px-6 py-4 text-center">
                          {getPrioridadeBadge(item.prioridade)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
